import React, { useEffect, useState, useContext } from "react";
import { 
  Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, 
  TableRow, IconButton, CircularProgress, TablePagination 
} from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import PageSearchBar from "../../common/PageSearchBar";
import { listTopics } from "../../utils/blogApi";
import ModalContext from "../../context/ModalContext.js";
import DrawerContext from "../../context/DrawerContext.js";
import "../../Pages/Topics/Topics.scss";

const Topics = () => {
  const modalCtx = useContext(ModalContext);
  const drawerCtx = useContext(DrawerContext);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => { getAllTopics(); }, []);
  useEffect(() => { getAllTopics(); }, [drawerCtx, modalCtx]);
  useEffect(() => { setPage(0); }, [searchInput]);

  const getAllTopics = () => {
    listTopics()
      .then((data) => setTopics(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error(err);
        setTopics([]);
        setLoadError(err.message);
      })
      .finally(() => setLoading(false));
  };

  const openDeleteTopicModal = (topic) => {
    modalCtx.setDetails("delete-topic", { slug: topic.slug, name: topic.name });
    modalCtx.openModal();
  };

  const toggleDrawerHandler = (drawer, data = {}) => {
    drawerCtx.setDetails(drawer, { type: drawer, ...data }); 
    drawerCtx.openDrawer();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredTopics = topics.filter((topic) => {
    const term = (searchInput || "").toLowerCase().trim();
    if (!term) return true;
    const title = (topic.name || "").toLowerCase();
    return title.includes(term);
  });

  const paginatedTopics = filteredTopics.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <div className="topics-container">
      <div className="header-section">
        <span className="title">TOPICS</span>
        <div className="header-items-div">
          <PageSearchBar value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search" />
          <Button variant="contained" className="add-button" onClick={() => toggleDrawerHandler("add-topic", null)}>+ ADD TOPIC</Button>
        </div>
      </div>
      <section className="topics-table-container">
        <TableContainer component={Paper} className="topics-table">
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell className="topics-head-cell">Topic Name</TableCell>
                <TableCell className="topics-head-cell" align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={2} align="center" style={{ border: "0px" }}><CircularProgress size={48} style={{ color: "#e84118",border: "0px" }} /></TableCell>
                </TableRow>
              ) : filteredTopics.length > 0 ? (
                paginatedTopics.map((topic) => (
                  <TableRow key={topic.slug} className="topics-table-row">
                    <TableCell className="topics-table-cell">{topic.name || "Unnamed Topic"}</TableCell>
                    <TableCell align="right">
                      <IconButton className="action-icon" size="small" onClick={() => toggleDrawerHandler("edit-topic", topic)}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton className="action-icon" size="small" onClick={() => openDeleteTopicModal(topic)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    {loadError || (topics.length === 0 ? "No Topics Found" : "No matching topics")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </section>
      <TableContainer component={Paper} className="topics-pagination">
        <Table>
          <TableBody>
            <TableRow>
              <TablePagination 
                rowsPerPageOptions={[10, 25, 50]} 
                colSpan={3} 
                count={filteredTopics.length} 
                rowsPerPage={rowsPerPage} 
                page={page}
                onPageChange={handleChangePage} 
                onRowsPerPageChange={handleChangeRowsPerPage} 
              />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default Topics;
